// SAB-632 pre-join disclosure (certificate A-1 / UIGEA: prizes established and made known
// in advance). Renders the payout structure a prospective member sees BEFORE joining.
//
// RENDER-FROM-FROZEN-TABLES DISCIPLINE (Phase 6 evidence item): when state='frozen' the
// rows come verbatim from league_prize_schedule (resolved_amount / frozen_pool) and are
// displayed AS-IS — this component never recomputes dollar amounts from percentages.
// Provisional (pre-lock) rows are league_bonuses percentages, labeled adjustable-until-lock.
// 'finalizing' (locked but nothing frozen — needs_attention or a grandfathered pre-M-series
// lock) deliberately shows no numbers; such leagues are unjoinable at the DB layer anyway.

export type PrizeScheduleState = 'provisional' | 'frozen' | 'finalizing'

export interface PrizeScheduleRow {
  label: string
  final_rank: number | null
  allocation_pct: number | null
  resolved_amount: number | null
  frozen_pool: number | null
}

const fmtPct = (p: number | null) =>
  p === null ? '—' : `${Number(p) % 1 === 0 ? Number(p).toFixed(0) : Number(p)}%`
const fmtUsd = (d: number | null) =>
  d === null ? '—' : `$${Number(d).toFixed(2)}`

export function prizeScheduleSummary(state: PrizeScheduleState, rows: PrizeScheduleRow[]): string | null {
  const ranked = rows.filter((r) => r.final_rank !== null)
  if (state === 'finalizing' || ranked.length === 0) return null
  const parts = [...ranked]
    .sort((a, b) => (a.final_rank ?? 0) - (b.final_rank ?? 0))
    .map((r) => (state === 'frozen' ? fmtUsd(r.resolved_amount) : fmtPct(r.allocation_pct)))
  return `Pays top ${ranked.length} · ${parts.join('/')}`
}

export function PrizeScheduleDisclosure({
  state,
  rows,
  entryFee,
  prizePool,
}: {
  state: PrizeScheduleState
  rows: PrizeScheduleRow[]
  entryFee: number
  prizePool: number | null
}) {
  const sorted = [...rows].sort(
    (a, b) => (a.final_rank ?? Number.MAX_SAFE_INTEGER) - (b.final_rank ?? Number.MAX_SAFE_INTEGER),
  )
  const frozenPool = state === 'frozen' ? sorted[0]?.frozen_pool ?? prizePool : null

  return (
    <div className="p-2.5 rounded-lg bg-muted/30 border border-border">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">Prize payout</p>
        {state === 'frozen' ? (
          <span className="text-[10px] font-bold text-green-400">Locked</span>
        ) : state === 'provisional' ? (
          <span className="text-[10px] font-bold text-amber-400">Until draft completes</span>
        ) : null}
      </div>

      {state === 'finalizing' ? (
        <p className="text-xs text-muted-foreground">
          Prize schedule is being finalized. This league is closed to new joins.
        </p>
      ) : sorted.length === 0 ? (
        <p className="text-xs text-muted-foreground">No prize structure configured yet.</p>
      ) : (
        <>
          <div className="space-y-0.5">
            {sorted.map((r, i) => (
              <div key={`${r.label}-${i}`} className="flex items-center justify-between text-xs">
                <span className="text-foreground">{r.label}</span>
                <span className="font-bold text-foreground">
                  {state === 'frozen' ? fmtUsd(r.resolved_amount) : fmtPct(r.allocation_pct)}
                </span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground mt-1.5 leading-relaxed">
            {state === 'frozen' ? (
              <>Amounts are locked{frozenPool !== null ? <> · Pool: {fmtUsd(frozenPool)} (fixed)</> : null}. The
              prize schedule froze when the league locked and cannot change.</>
            ) : (
              <>Percentages of the final prize pool. The pool is all{' '}
              {entryFee > 0 ? `$${entryFee} entry fees` : 'entry fees'} collected before the league locks
              {prizePool !== null ? <> (currently {fmtUsd(prizePool)})</> : null}; the commissioner can
              adjust this structure until the draft completes, when it locks permanently. Nothing
              purchased during the season changes the pool.</>
            )}
          </p>
        </>
      )}
    </div>
  )
}
